import {Phone, Calendar, Shield, Check, Zap, MapPin, Car, Award} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const TeslaModel3PpfPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does Tesla Model 3 PPF cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front coverage starts at $1,299, full front coverage starts at $1,899, and full vehicle PPF starts at $4,499. Pricing varies based on paint condition and coverage selection. Larger vehicles and specialty films may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "Should I get PPF on a new Model 3?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Model 3 paint chips easily due to thin water-based coating. Installing PPF before highway driving prevents permanent damage and preserves resale value from day one."
          }
        },
        {
          "@type": "Question",
          "name": "Will PPF yellow on my white or black Model 3?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Premium PPF includes UV inhibitors and a 10-year warranty against yellowing. Our film remains crystal clear on white, black, and all Model 3 colors throughout the warranty period."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Tesla Model 3 PPF San Diego | Paint Protection Film Installation"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert Tesla Model 3 PPF installation in San Diego. Protect your Model 3 with professional paint protection film. Self-healing technology, manufacturer-backed film warranties. Serving Vista, Carlsbad, Oceanside.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/tesla-model-3-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Tesla Model 3 PPF San Diego - Professional Paint Protection Film Installation</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your Tesla Model 3 with expert paint protection film installation in San Diego. Our installers specialize in Model 3-specific coverage, delivering careful protection against rock chips, door dings, and everyday wear.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              The Model 3 is San Diego's most popular Tesla, and for good reason—but its thin water-based paint requires professional protection. We use precision-cut PPF templates designed specifically for Model 3's aerodynamic contours, ensuring invisible coverage that preserves your sedan's sleek appearance while preventing costly paint damage.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Model 3 PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Model 3 Needs PPF */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Tesla Model 3 Owners in San Diego Need PPF</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              The Tesla Model 3 revolutionized electric vehicles with its performance, technology, and affordability—but Model 3 owners quickly discover that Tesla's paint is notoriously thin and soft. San Diego's aggressive driving conditions, coastal salt air, and year-round sun exposure make <a href="/paint-protection-film" className="text-cyan-400 hover:underline">paint protection film</a> essential for maintaining your Model 3's pristine appearance and strong resale value.
            </p>
            <p>
              Model 3 sedans face unique vulnerability: the low front bumper and aerodynamic design direct road debris straight onto the paint surface at highway speeds. Without protection, Model 3 owners in Vista, Carlsbad, and Oceanside see rock chips appear within the first few thousand miles of I-5 and SR-78 commuting. PPF creates an invisible shield that absorbs impacts and prevents permanent paint damage.
            </p>
            <p>
              Our Model 3 PPF installations use bulk film or precision-cut patterns selected for each body panel and trim piece. We protect critical areas including the aggressive front fascia, low rocker panels (especially vulnerable on lowered Performance models), door edges, rear bumper, and trunk lip. The result is comprehensive protection that's designed to stay discreet and low-maintenance for years.
            </p>
            <p>
              Model 3 owners frequently combine <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">PPF</a> with <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> for the popular blacked-out aesthetic, plus <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> for heat rejection and privacy. We offer package pricing for complete Model 3 protection and customization in one appointment at our Vista facility.
            </p>
          </div>
        </div>
      </section>

      {/* Model 3 Coverage Options & Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model 3 PPF Coverage & Pricing</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Essential Model 3 protection targeting the most vulnerable areas: front bumper, partial hood, mirrors, and headlights. Ideal for daily commuters and budget-conscious protection.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full front bumper coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Side mirrors and headlights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Self-healing top coat</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Protection</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive Model 3 front-end shield: full hood, bumper, fenders, mirrors, and lights. Maximum highway protection for commuters driving I-5, I-15, and SR-78 daily.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All partial coverage included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Rocker panel option available</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Protection</h3>
              <p className="text-gray-300 mb-6">
                Complete Model 3 protection covering every painted surface. Ultimate investment protection for new vehicles and maximum resale value for years to come.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Exact pricing after consultation</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front areas covered</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rockers, rear quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Lifetime paint preservation</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            All packages include professional paint preparation, precision installation, and manufacturer warranty. Starting prices shown are for Tesla Model 3. Exact pricing determined after free consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Model 3 Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model 3-Specific PPF Protection Benefits</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Front Fascia Shield</h3>
              <p className="text-gray-300">Model 3's aggressive front bumper design takes direct rock chip hits. PPF prevents paint damage that would otherwise require expensive bumper repainting.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Rocker Panel Armor</h3>
              <p className="text-gray-300">Lowered stance on Performance models makes rockers prone to curb rash and road spray damage. PPF shields these vulnerable side panels.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Surface</h3>
              <p className="text-gray-300">Minor scratches and swirl marks disappear with heat from sun or warm water, keeping your Model 3 looking new through years of San Diego driving.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Resale Value Protection</h3>
              <p className="text-gray-300">Model 3 with protected paint commands $2,000-$5,000 premium at resale. Buyers pay more for vehicles showing zero paint wear and flawless condition.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model 3 PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Model 3 Consultation & Quote</h3>
                <p className="text-gray-300">We inspect your Model 3's paint condition, discuss your driving habits (daily commute, road trips, track days), and recommend coverage options matching your protection needs and budget. You receive a transparent quote with detailed breakdowns—no surprises or hidden fees.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Surface Correction</h3>
                <p className="text-gray-300">Your Model 3 receives thorough washing with pH-balanced automotive shampoo, clay bar decontamination to remove embedded particles, and paint correction if needed. New Model 3 deliveries often have swirl marks from transport—we remove these before PPF application. Used vehicles receive full paint polishing to restore a flawless base.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision PPF Application</h3>
                <p className="text-gray-300">Using Model 3-specific bulk or precision-pattern methods, we apply premium self-healing PPF with professional wet installation technique. All edges are properly wrapped or tucked for invisible seams. We mask cameras, sensors, charging port, and door handles, then reinstall trim pieces for seamless edge-to-edge coverage.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Final Inspection & Delivery</h3>
                <p className="text-gray-300">Multi-point quality check ensures perfect edges, zero bubbles, complete adhesion, and proper sensor function. We verify all cameras and Autopilot hardware work correctly, provide maintenance instructions, deliver warranty documentation, and review care guidelines before returning your protected Model 3.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Model 3 Variants */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF for All Model 3 Variants</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Model 3 Standard Range / RWD</h3>
              <p className="text-gray-300 mb-4">Perfect daily driver protection for the most affordable Tesla. We protect your Model 3 RWD with full or partial front coverage, keeping your efficient commuter looking new through years of San Diego driving.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Budget-friendly PPF packages</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Front bumper priority coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Maintains resale value</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Model 3 Long Range / AWD</h3>
              <p className="text-gray-300 mb-4">Premium trim deserves premium protection. Model 3 LR owners typically choose full front or full vehicle PPF to match their higher investment and frequent long-distance travel needs.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Full front coverage recommended</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Road trip protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Premium film options</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Model 3 Performance</h3>
              <p className="text-gray-300 mb-4">Track-capable performance demands maximum protection. We offer full vehicle PPF for Performance Model 3, including lowered rocker panels, aggressive front splitter, and carbon fiber spoiler.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Full vehicle coverage popular</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Lowered rocker protection critical</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Track day ready</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Protection Benefits */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Model 3 Paint Protection Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Highway Rock Chip Prevention</h3>
              <p className="text-gray-300 mb-4">San Diego Model 3 owners drive I-5, I-15, and I-805 daily, encountering constant gravel, rocks, and debris kicked up by trucks and construction vehicles. Without PPF, your front bumper and hood develop dozens of chips within the first year.</p>
              <p className="text-gray-300">PPF absorbs these impacts, preventing permanent paint damage and maintaining the pristine appearance that keeps your Model 3's resale value strong.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Door Edge & Handle Protection</h3>
              <p className="text-gray-300 mb-4">Model 3's flush door handles and painted door edges chip easily from rings, keys, nails, and contact with adjacent vehicles in crowded San Diego parking structures and beach lots.</p>
              <p className="text-gray-300">Door edge PPF prevents these everyday scratches and chips, eliminating the need for costly touch-up paint or panel repainting throughout ownership.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Bug Splatter & Tar Protection</h3>
              <p className="text-gray-300 mb-4">Coastal driving and summer road trips leave Model 3 front ends covered in bug residue, tar spots, and road grime. Aggressive cleaning to remove these contaminants damages unprotected paint over time.</p>
              <p className="text-gray-300">PPF creates a sacrificial barrier that withstands harsh cleaners and pressure washing, allowing safe removal of bugs and tar without damaging your Model 3's paint finish.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">UV & Oxidation Defense</h3>
              <p className="text-gray-300 mb-4">San Diego's year-round intense sunlight fades and oxidizes unprotected Tesla paint, especially darker colors. UV damage accumulates daily, dulling your Model 3's shine and reducing gloss depth.</p>
              <p className="text-gray-300">Premium PPF includes UV inhibitors that block harmful rays, preventing oxidation and color fade for 10+ years of permanent gloss and depth protection.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Model 3 PPF FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does Tesla Model 3 PPF cost in San Diego?</h3>
              <p className="text-gray-300">Partial front coverage starts at $1,299, full front coverage starts at $1,899, and full vehicle PPF starts at $4,499. Final pricing depends on your Model 3 variant (RWD, Long Range, Performance), paint condition, and chosen coverage — larger vehicles and specialty films may cost more. We provide itemized quotes with complete transparency.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Should I get PPF on a new Model 3?</h3>
              <p className="text-gray-300">Yes. Model 3 paint chips easily due to thin water-based coating. Installing PPF before your first highway drive prevents permanent damage and preserves factory paint from mile one. Most San Diego buyers protect their new Model 3 within the first month of ownership for maximum long-term value.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does Model 3 PPF installation take?</h3>
              <p className="text-gray-300">Partial front takes 1 day, full front requires 2 days, and full vehicle PPF takes 3-5 days. Installation time includes proper paint preparation, careful application, and adequate curing. We never rush quality—patience ensures bubble-free results that last 10+ years.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF yellow on my white or black Model 3?</h3>
              <p className="text-gray-300">Premium PPF includes UV inhibitors and a manufacturer warranty against yellowing, cracking, and discoloration. Coverage terms depend on the film selected. Our film is designed to stay clear on Pearl White Multi-Coat, Midnight Silver, Solid Black, and all Model 3 colors throughout the applicable warranty period.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be applied to a used Model 3?</h3>
              <p className="text-gray-300">Absolutely. We perform paint correction to remove existing swirl marks, water spots, and light scratches before PPF installation, restoring a like-new finish. PPF then locks in the corrected paint and prevents future damage. Many used Model 3 buyers protect their investment immediately after purchase.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I combine Model 3 PPF with chrome delete?</h3>
              <p className="text-gray-300">Yes! The blacked-out Model 3 look is extremely popular in San Diego. Combining <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a>, <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a>, <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">window tint</a>, and <a href="/caliper-painting" className="text-cyan-400 hover:underline">caliper painting</a> creates a fully customized, protected Model 3. We offer package discounts for multi-service installations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Tesla Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/tesla-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Professional PPF for all Tesla models including Model S, X, Y, and Cybertruck</p>
            </a>
            <a href="/paint-protection-film-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Premium PPF installation for all vehicle makes and models</p>
            </a>
            <a href="/chrome-delete-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete San Diego</h3>
              <p className="text-gray-400 text-sm">Blacked-out trim and window surrounds for sleek Model 3 customization</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Tesla Model 3 PPF Serving All San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in Vista, we provide expert Model 3 paint protection film services throughout North County San Diego, coastal communities, and the entire San Diego metro area.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-center">
            <div>
              <p className="font-semibold text-white mb-2">North County</p>
              <p className="text-sm"><a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> • <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a> • <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a> • <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a> • <a href="/ppf-san-marcos" className="text-cyan-400 hover:underline">San Marcos</a> • <a href="/ppf-escondido" className="text-cyan-400 hover:underline">Escondido</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">Coastal</p>
              <p className="text-sm"><a href="/ppf-del-mar" className="text-cyan-400 hover:underline">Del Mar</a> • <a href="/ppf-solana-beach" className="text-cyan-400 hover:underline">Solana Beach</a> • <a href="/ppf-la-jolla" className="text-cyan-400 hover:underline">La Jolla</a> • <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></p>
            </div>
            <div>
              <p className="font-semibold text-white mb-2">East County</p>
              <p className="text-sm"><a href="/ppf-poway" className="text-cyan-400 hover:underline">Poway</a> • Ramona • Santee • <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Tesla Model 3 with Professional PPF</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free Model 3 PPF consultation today. Get a custom quote and same-week installation appointments for San Diego County residents.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Model 3 PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default TeslaModel3PpfPage
